// src/components/AddItemModal.js
// Reads addon groups from Firestore
// Shows only addons relevant to selected size (Personal/Share/Family)
// Groups addons by type — Base (single select) / Cheese & Toppings (multi select)

import React, { useState, useEffect } from 'react';
import { autoSizePrice } from '../utils/money';
import { optionLabel } from '../utils/addons';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { SIZES } from '../data/menuData';

export default function AddItemModal({ item, onClose, onAdd }) {
  const sizes = SIZES[item.sizes] || SIZES.single;
  const [selSize, setSelSize]         = useState(sizes[0]);
  const [addonGroups, setAddonGroups] = useState([]);
  const [selections, setSelections]   = useState({}); // { groupId: optionId | [optionId] }
  const [loading, setLoading]         = useState(true);

  // Load addon groups from Firestore once
  useEffect(() => {
    getDocs(collection(db, 'addonGroups')).then(snap => {
      const groups = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .filter(g => {
          // Only show groups that apply to this item type
          if (!g.appliesTo || g.appliesTo.includes('all')) return true;
          return g.appliesTo.some(t => item.sizes?.includes(t) || item.cat?.toLowerCase().includes(t));
        })
        .sort((a, b) => (a.order || 0) - (b.order || 0));
      setAddonGroups(groups);
      setLoading(false);
    });
  }, [item.id]);

  // Reset selections when size changes
  useEffect(() => { setSelections({}); }, [selSize]);

  // Get price for an option based on selected size
  const getOptionPrice = (option) => {
    const sizeLabel = selSize?.label || 'Personal';
    if (sizeLabel === 'Personal') return option.personalPrice || 0;
    if (sizeLabel === 'Share')    return option.sharePrice    || 0;
    if (sizeLabel === 'Family')   return option.familyPrice   || 0;
    return option.personalPrice || 0;
  };

  // Toggle selection for single/multiple groups
  const toggleSelection = (group, optionId) => {
    setSelections(prev => {
      if (group.selectionType === 'single') {
        // Deselect if already selected, else select
        return { ...prev, [group.id]: prev[group.id] === optionId ? null : optionId };
      } else {
        // Multiple select
        const current = prev[group.id] || [];
        const exists  = current.includes(optionId);
        return { ...prev, [group.id]: exists ? current.filter(x => x !== optionId) : [...current, optionId] };
      }
    });
  };

  const isSelected = (group, optionId) => {
    const sel = selections[group.id];
    if (group.selectionType === 'single') return sel === optionId;
    return (sel || []).includes(optionId);
  };

  // Price for the selected size — use an admin-set override if present, else fall back to basePrice × multiplier
  const getSizePrice = (size) => {
    const override = item.sizePrices?.[size?.label];
    return override != null && override !== '' ? +override : autoSizePrice(item.basePrice, size?.mult || 1);
  };

  // Calculate total
  const basePrice = getSizePrice(selSize);
  const addonTotal = addonGroups.reduce((total, group) => {
    const sel = selections[group.id];
    if (!sel) return total;
    const ids = group.selectionType === 'single' ? [sel] : sel;
    return total + ids.reduce((sum, optId) => {
      const opt = (group.options || []).find(o => o.id === optId);
      return sum + (opt ? getOptionPrice(opt) : 0);
    }, 0);
  }, 0);
  const grandTotal = basePrice + addonTotal;

  // Build addon summary for cart
  const getAddonSummary = () => {
    const addons = [];
    addonGroups.forEach(group => {
      const sel = selections[group.id];
      if (!sel) return;
      const ids = group.selectionType === 'single' ? (sel ? [sel] : []) : (sel || []);
      ids.forEach(optId => {
        const opt = (group.options || []).find(o => o.id === optId);
        if (opt) addons.push({ name: optionLabel(opt), price: getOptionPrice(opt), groupName: group.name });
      });
    });
    return addons;
  };

  const handleAdd = () => {
    const addons    = getAddonSummary();
    const addonAmt  = addons.reduce((a, x) => a + x.price, 0);
    onAdd({
      menuId:    item.id,
      name:      item.name + (selSize.label !== 'Regular' ? ` (${selSize.label})` : ''),
      price:     basePrice + addonAmt,
      qty:       1,
      addons:    addons.map(a => a.name),
      sizeLabel: selSize.label,
      sizeBasePrice: basePrice, // ⚠️ REQUIRED for weekly buy1get1 offer eligibility detection
    });
    onClose();
  };

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', zIndex: 500,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-start',
        paddingTop: 70, paddingLeft: 210,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 12, padding: 18, width: 400,
          maxHeight: '82vh', overflowY: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,.18)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 14 }}>
          {item.emoji} {item.name}
        </div>

        {/* Size selector */}
        {item.hasAddon !== false && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#888', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 7 }}>
              Select Size
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {sizes.map((s, i) => {
                const price = getSizePrice(s);
                const active = selSize?.label === s.label;
                return (
                  <div key={i} onClick={() => setSelSize(s)} style={{
                    padding: '7px 11px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                    border: `1.5px solid ${active ? '#D85A30' : '#eee'}`,
                    background: active ? '#FAECE7' : '#fff',
                    color: active ? '#993C1D' : '#333',
                  }}>
                    <div style={{ fontWeight: 600 }}>{s.label}</div>
                    <div style={{ color: '#D85A30', fontSize: 11, marginTop: 1 }}>₹{price}</div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Addon groups */}
        {item.hasAddon && !loading && addonGroups.map(group => {
          const activeOptions = (group.options || []).filter(o => o.active !== false);
          if (!activeOptions.length) return null;
          return (
            <div key={group.id} style={{ marginBottom: 14 }}>
              <div style={{
                fontSize: 12, fontWeight: 600, color: '#888',
                textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 7,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center'
              }}>
                <span>{group.name}</span>
                <span style={{ fontSize: 10, fontWeight: 400, background: group.selectionType === 'single' ? '#E6F1FB' : '#EAF3DE', color: group.selectionType === 'single' ? '#185FA5' : '#3B6D11', padding: '2px 7px', borderRadius: 10 }}>
                  {group.selectionType === 'single' ? 'Choose one' : 'Choose many'}
                </span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                {activeOptions.map(opt => {
                  const price   = getOptionPrice(opt);
                  const selected = isSelected(group, opt.id);
                  return (
                    <div key={opt.id} onClick={() => toggleSelection(group, opt.id)} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '7px 10px', borderRadius: 8, cursor: 'pointer', fontSize: 12,
                      border: `1.5px solid ${selected ? '#D85A30' : '#eee'}`,
                      background: selected ? '#FAECE7' : '#fff',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{
                          width: 14, height: 14, borderRadius: group.selectionType === 'single' ? '50%' : 3,
                          border: `2px solid ${selected ? '#D85A30' : '#ddd'}`,
                          background: selected ? '#D85A30' : '#fff',
                          flexShrink: 0,
                        }} />
                        <span style={{ fontWeight: selected ? 500 : 400 }}>{optionLabel(opt)}</span>
                      </div>
                      <span style={{ color: price > 0 ? '#D85A30' : '#aaa', fontWeight: 500, marginLeft: 4 }}>
                        {price > 0 ? `+₹${price}` : 'Free'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {loading && item.hasAddon && (
          <div style={{ textAlign: 'center', color: '#aaa', fontSize: 13, padding: '10px 0' }}>
            Loading add-ons…
          </div>
        )}

        {/* Addon summary */}
        {getAddonSummary().length > 0 && (
          <div style={{ background: '#f8f8f8', borderRadius: 8, padding: '8px 11px', marginBottom: 12, fontSize: 12 }}>
            <div style={{ color: '#888', marginBottom: 4 }}>Selected add-ons:</div>
            {getAddonSummary().map((a, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#555' }}>
                <span>{a.name}</span>
                <span style={{ color: '#D85A30' }}>+₹{a.price}</span>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          paddingTop: 12, borderTop: '1px solid #eee',
        }}>
          <div style={{ fontWeight: 700, fontSize: 15 }}>
            Total: <span style={{ color: '#D85A30' }}>₹{grandTotal}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleAdd}>Add to Bill</button>
          </div>
        </div>
      </div>
    </div>
  );
}